import React, { useState, useEffect, useRef } from 'react'
import { Modal, Box, Typography, IconButton, Button } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import EdTextfield from '@/components/forms/EdTextfield'

interface UploadMaterialModalProps {
  isOpen: boolean
  onClose: () => void
  materialType: 'video' | 'scorm' | 'pdf'
}

const UploadMaterialModal: React.FC<UploadMaterialModalProps> = ({ isOpen, onClose, materialType }) => {
  const [dragActive, setDragActive] = useState(false)
  const [videoUrl, setVideoUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setVideoUrl('')
      setSelectedFile(null)
      setDragActive(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [isOpen])

  const getModalTitle = () => {
    switch (materialType) {
      case 'video': return 'Carica Video'
      case 'scorm': return 'Carica SCORM'
      case 'pdf': return 'Carica PDF'
      default: return 'Carica Materiale'
    }
  }

  const getFileExtensions = () => {
    switch (materialType) {
      case 'video': return '.mp4, .avi, .mov, .wmv'
      case 'scorm': return '.zip, .rar'
      case 'pdf': return '.pdf'
      default: return ''
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0])
      if (materialType === 'video') {
        setVideoUrl('') // Clear URL if file is selected
      }
      console.log('File dropped:', e.dataTransfer.files[0])
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
      if (materialType === 'video') {
        setVideoUrl('') // Clear URL if file is selected
      }
      console.log('File selected:', e.target.files[0])
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVideoUrl(e.target.value)
    if (e.target.value) {
      setSelectedFile(null) // Clear file if URL is entered
      if (fileInputRef.current) {
        fileInputRef.current.value = '' // Reset file input
      }
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="upload-material-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '600px' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <div className="flex items-center justify-between">
            <Typography id="upload-material-modal-title" variant="h6" component="h2" sx={{ color: '#4F4F4F', fontWeight: 'bold' }}>
              {getModalTitle()}
            </Typography>
            <span onClick={onClose}>
              <i className="ri-close-line text-gray-500"></i>
            </span>
          </div>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1 }}>
          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              materialType === 'video' && videoUrl 
                ? 'border-gray-200 bg-gray-50 opacity-50 pointer-events-none' 
                : dragActive 
                  ? 'border-[#B80F15] bg-red-50' 
                  : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <i className={`ri-${materialType === 'video' ? 'video' : materialType === 'scorm' ? 'file-zip' : 'file-pdf'}-line text-2xl ${selectedFile ? 'text-[#B80F15]' : 'text-gray-400'}`}></i>
              </div>
              <div>
                {selectedFile ? (
                  <>
                    <p className="text-lg font-medium text-[#B80F15] mb-2">
                      File selezionato: {selectedFile.name}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-medium text-[#4F4F4F] mb-2">
                      Trascina il file qui o clicca per selezionare
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Formati supportati: {getFileExtensions()}
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={materialType === 'video' ? 'video/*' : materialType === 'scorm' ? '.zip' : '.pdf'}
                  onChange={handleFileInput}
                  className="hidden"
                  id="file-upload"
                  disabled={materialType === 'video' && !!videoUrl}
                />
                <label
                  htmlFor="file-upload"
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    materialType === 'video' && videoUrl
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#B80F15] text-white cursor-pointer hover:bg-[#A00D13]'
                  }`}
                >
                  Scegli File
                </label>
              </div>
            </div>
          </div>

          {/* Video URL Alternative - Only for videos */}
          {materialType === 'video' && (
            <div className="mt-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-gray-500 text-sm font-medium">oppure</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
              <div className={selectedFile ? 'opacity-50 pointer-events-none' : ''}>
                <EdTextfield 
                  id="video-url"
                  label="Link Video"
                  type="url"
                  placeholder="Inserisci l'URL del video (es. https://...)"
                  register={{
                    value: videoUrl,
                    onChange: handleUrlChange
                  }}
                  disabled={!!selectedFile}
                />
              </div>
            </div>
          )}

          {/* Additional Fields */}
          <div className="mt-6 space-y-4">
            <EdTextfield 
              id="material-title"
              label="Titolo"
              type="text"
              placeholder="Inserisci il titolo del materiale"
            />
            <EdTextfield 
              id="material-description"
              label="Descrizione"
              type="text"
              placeholder="Inserisci una descrizione"
            />
            <EdTextfield 
              id="reading-duration"
              label="Durata lettura (minuti)"
              type="number"
              placeholder="Inserisci la durata di lettura"
            />
          </div>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          p: 3,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              backgroundColor: '#E3EBF7',
              color: '#285192',
              textTransform: 'uppercase',
              fontSize: '12px',
              fontWeight: 'medium',
              px: 3,
              py: 1,
              border: 'none',
              '&:hover': {
                backgroundColor: '#BDBDBD',
              },
            }}
          >
            ANNULLA
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#B80F15',
              textTransform: 'uppercase',
              fontSize: '12px',
              fontWeight: 'bold',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#A00D13',
              },
            }}
          >
            CARICA
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default UploadMaterialModal
